import { Check } from '@phosphor-icons/react';
import { pickLocale, type Locale } from '@shh/shared';
import { Button, ChoiceGroup, Text } from '@shh/ui';
import { useState } from 'react';
import { updateHousehold } from '../../data/api';
import { useFamily } from '../../data/family';
import { languageChoices, languageText as t } from '../../i18n/language';
import { locale, setDeviceLocale } from '../../lib/locale';

type Scope = 'device' | 'family';

/**
 * Settings → Sprache: one choice, then "for whom?" – only this device, or the whole family
 * (reminders, mails, templates, AI suggestions; Mike, 14.10.). Only parents decide for the family.
 * Applying a new device language restarts the app in it.
 */
export function LanguageSettings() {
  const f = useFamily();
  const family = pickLocale(f.household?.locale);
  const forFamily = f.role === 'parent' || f.role === 'device';
  const [value, setValue] = useState<Locale>(locale);
  const [scope, setScope] = useState<Scope>('device');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(false);
  const unchanged = value === locale && (scope === 'device' || value === family);

  const apply = async () => {
    setBusy(true);
    setError(false);
    setSaved(false);
    try {
      if (scope === 'family') await updateHousehold(f.householdId, { locale: value });
      // a new device language restarts the app; otherwise say that it is saved
      if (value !== locale) setDeviceLocale(value);
      else setSaved(true);
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-gap-lg">
      <ChoiceGroup<Locale>
        label={t.language}
        value={value}
        onChange={(v) => {
          setValue(v);
          setSaved(false);
        }}
        choices={languageChoices}
      />
      {forFamily && (
        <ChoiceGroup<Scope>
          label={t.forWhom}
          value={scope}
          onChange={(v) => {
            setScope(v);
            setSaved(false);
          }}
          choices={[
            { value: 'device', label: t.device, description: t.deviceHint },
            { value: 'family', label: t.family, description: t.familyHint },
          ]}
        />
      )}
      <div className="flex flex-wrap items-center gap-gap-md">
        <Button icon={Check} isDisabled={busy || unchanged} onPress={() => void apply()}>
          {t.apply}
        </Button>
        {saved && (
          <span role="status" className="text-sm font-semibold text-feedback-success">
            {t.saved}
          </span>
        )}
      </div>
      {error && (
        <p role="alert" className="font-semibold text-feedback-danger">
          {t.failed}
        </p>
      )}
      <Text size="sm" tone="muted">
        {forFamily ? t.current(t.names[locale], t.names[family]) : t.currentDevice(t.names[locale])}
      </Text>
    </div>
  );
}
